import React, { useState, useMemo } from "react";
import {
  ShoppingCart,
  Zap,
  Moon,
  Search,
  Info,
  LineChart as LineIcon,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Tooltip,
  Legend,
  XAxis,
  YAxis,
  BarChart,
  Bar,
} from "recharts";
import { useSelector } from "react-redux";
import { TbBuildingWarehouse } from "react-icons/tb";

const inventoryData = [
  {
    id: "SIDCGL-009",
    name: "CI Air Release Valve 80mm",
    category: "Electronics",
    unit: 500,
    location: "Goa Industrial Warehouse",
    onHand: 4.0,
    forecast: -1.0,
    route: "Buy",
    min: 2.0,
    max: 10.0,
    toOrder: 1.0,
    soldUnits: 120,
  },
  {
    id: "WRD-0007",
    name: "Flow Meter of Filter Bed",
    category: "Electronics",
    location: "Panchwadi Utilities Warehouse",
    unit: 100,
    onHand: 1.0,
    forecast: -4.0,
    route: "Manufacture",
    min: 5.0,
    max: 15.0,
    toOrder: 4.0,
    soldUnits: 150,
  },
  {
    id: "TM-6000",
    name: "Transit Mixer",
    category: "Electronics",
    location: "Pune Equipment Warehouse",
    unit: 600,
    onHand: 5.0,
    forecast: -6.0,
    route: "Buy",
    min: 10.0,
    max: 20.0,
    toOrder: 6.0,
    soldUnits: 10,
  },
  {
    id: "BTM-7500",
    name: "Blower",
    category: "Electronics",
    location: "Pune Equipment Warehouse",
    unit: 400,
    onHand: 2.0,
    forecast: -1.0,
    route: "",
    min: 0.0,
    max: 0.0,
    toOrder: 1.0,
    soldUnits: 80,
  },
  {
    id: "WRD-0007",
    name: "Concrete pump",
    category: "Electronics",
    location: "Panchwadi Utilities Warehouse",
    unit: 500,
    onHand: 4.0,
    forecast: 4.0,
    route: "Buy",
    min: 5.0,
    max: 10.0,
    toOrder: 6.0,
    soldUnits: 200,
  },
  {
    id: "SIDCGL-010",
    name: "DI Pipe Tee 250x80mm DS",
    category: "Electronics",
    location: "Goa Industrial Warehouse",
    unit: 7000,
    onHand: 8.0,
    forecast: 8.0,
    route: "",
    min: 10.0,
    max: 10.0,
    toOrder: 2.0,
    soldUnits: 5,
  },
  {
    id: "MIX-RM1400",
    name: "Mechanical Mixer – Reversible ...",
    category: "Electronics",
    location: "Pune Equipment Warehouse",
    unit: 600,
    onHand: 12.0,
    forecast: 12.0,
    route: "Manufacture",
    min: 0.0,
    max: 0.0,
    toOrder: 0.0,
    soldUnits: 15,
  },
 

];

const WarehouseAnalytics = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const themeMode = useSelector((state) => state.theme.mode);
  const textColorClass = themeMode ? "dark_text-color" : "text-primary";
  const bgColorClass = themeMode ? "bg_light_main" : "bg-white";
  const borderThem = themeMode ? "border_them_2" : "border-primary";
  const tableHedBg = themeMode ? "table_hed_bg" : "tabel_hed_def_bg";
  const scrollBg = themeMode ? "sidebar_scroll_dark" : "sidebar_scroll";

  const sortedByUnits = useMemo(
    () => [...inventoryData].sort((a, b) => b.unit - a.unit),
    []
  );
  const fastMovingList = sortedByUnits.slice(0, 3);
  const slowMovingList = [...sortedByUnits].reverse().slice(0, 3);

  const pieData = useMemo(() => {
    const totalOnHand = inventoryData.reduce(
      (sum, item) => sum + item.onHand,
      0
    );
    const totalMax = inventoryData.reduce(
      (sum, item) => sum + (item.max || 10),
      0
    );
    const utilizedPercent = Math.min(
      Math.round((totalOnHand / totalMax) * 100),
      100
    );
    return [
      { name: "Utilized", value: utilizedPercent },
      { name: "Available", value: 100 - utilizedPercent },
    ];
  }, []);

  const categoryChartData = useMemo(() => {
    const categories = inventoryData.reduce((acc, item) => {
      acc[item.category] = (acc[item.category] || 0) + item.onHand;
      return acc;
    }, {});
    return Object.keys(categories).map((key) => ({
      name: key,
      stock: categories[key],
    }));
  }, []);

  const stockTrendData = [
    { name: "W1", value: 30000 },
    { name: "W2", value: 10000 },
    { name: "W3", value: 2300 },
    { name: "W4", value: totalByUnits() * 0.8 },
    { name: "W5", value: totalByUnits() },
  ];

  function totalByUnits() {
    return inventoryData.reduce((sum, item) => sum + item.unit, 0);
  }

  const handleAction = (action, productName) => {
    alert(`${action} initiated for ${productName}`);
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (selectedIds.length === inventoryData.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(inventoryData.map((item) => item.id));
    }
  };

  return (
    <div className={`w-full ${bgColorClass} h-screen flex flex-col font-sans`}>
      <div
        className={`py-4 md:px-12 px-6 border-b ${borderThem} flex justify-between items-center h-[57px] shrink-0`}
      >
        <div
          className={`flex ps-3 md:ps-0 text-sm md:text-base ${textColorClass} items-center gap-x-2`}
        >
          <TbBuildingWarehouse className="cursor-pointer font-semibold text-xl" />
          <p className="font-semibold">Warehouse & Stock Management</p>
        </div>
      </div>

      <div className={`flex-1 overflow-y-auto pb-10 ${scrollBg}`}>
        <div className="px-3 md:px-8 mt-4 md:mt-6">
          <main className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 animate-in fade-in duration-500">
              <MetricCard
                title="Warehouse Capacity"
                borderThem={borderThem}
                themeMode={themeMode}
              >
                <div className="h-48 relative flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        innerRadius={50}
                        outerRadius={70}
                        paddingAngle={5}
                        dataKey="value"
                        startAngle={90}
                        endAngle={450}
                      >
                        <Cell fill="#6366f1" />
                        <Cell fill="#e2e8f0" />
                      </Pie>
                      <Tooltip />
                      <Legend
                        iconType="circle"
                        verticalAlign="bottom"
                        height={36}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute top-[33%] flex flex-col items-center pointer-events-none">
                    <span className={`text-xl font-bold ${textColorClass}`}>
                      {pieData[0].value}%
                    </span>
                  </div>
                </div>
              </MetricCard>

              <MetricCard
                title="Stock Value"
                borderThem={borderThem}
                themeMode={themeMode}
              >
                <div className="flex justify-between items-center mb-1">
                  <h2
                    className={`text-2xl font-bold ${
                      themeMode ? "text-white" : "text-slate-800"
                    }`}
                  >
                    ₹{totalByUnits().toLocaleString()}
                  </h2>
                  <span className="text-[10px] text-emerald-500 font-bold bg-emerald-50 px-1.5 rounded">
                    +12%
                  </span>
                </div>
                <div className="h-36">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={stockTrendData}
                      margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="colorValue"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#6366f1"
                            stopOpacity={0.3}
                          />
                          <stop
                            offset="95%"
                            stopColor="#6366f1"
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>

                      <XAxis dataKey="name" hide />

                      <Tooltip
                        labelStyle={{
                          color: "#6366f1",
                          fontWeight: "bold",
                          marginBottom: "4px",
                        }}
                        contentStyle={{
                          borderRadius: "8px",
                          border: "none",
                          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                        }}
                      />

                      <Area
                        name="Stock Value"
                        type="monotone"
                        dataKey="value"
                        stroke="#6366f1"
                        fill="url(#colorValue)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </MetricCard>

              <MetricCard
                title="Low Stock Alerts"
                borderThem={borderThem}
                themeMode={themeMode}
              >
                <div className="relative w-full h-full">
                  {inventoryData
                    .filter((i) => i.forecast < 0)
                    .slice(0, 4)
                    .map((item, idx) => (
                      <div
                        key={idx}
                        className={`flex justify-between text-xs border-b ${borderThem} py-1`}
                      >
                        <span className={`${textColorClass} truncate mr-2`}>
                          [{item.id}] {item.name}
                        </span>
                        <span className="text-rose-500 font-bold">
                          {item.onHand}/{item.min || 1}
                        </span>
                      </div>
                    ))}
                  <button className="w-full bottom-5 cursor-pointer absolute py-2 bg-slate-900 text-white rounded-lg text-[10px] font-bold hover:bg-slate-800 transition-colors">
                    REORDER ALL
                  </button>
                </div>
              </MetricCard>
            </div>

            <div className="grid grid-cols-12 gap-3 h-full">
              <div className="md:col-span-7 col-span-12">
                <MetricCard
                  title="Product Velocity (By Units)"
                  borderThem={borderThem}
                  themeMode={themeMode}
                >
                  <div className="flex md:items-center flex-col md:flex-row gap-3 w-full md:justify-between py-2 md:max-h-40">
                    <div className="space-y-2 md:w-1/2">
                      <p className="text-[9px] text-slate-400 font-bold uppercase flex items-center gap-1">
                        <TrendingUp size={12} className="text-emerald-500" />{" "}
                        Fast Moving (Top 3 Units)
                      </p>
                      {fastMovingList.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/10"
                        >
                          <p
                            className={`text-[11px] font-bold ${textColorClass} truncate w-32`}
                          >
                            {item.name}
                          </p>
                          <span className="text-emerald-500 font-bold text-[10px]">
                            {item.unit} Units
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-2 md:w-1/2">
                      <p className="text-[9px] text-slate-400 font-bold uppercase flex items-center gap-1">
                        <TrendingDown size={12} className="text-rose-500" />{" "}
                        Slow Moving (Bottom 3 Units)
                      </p>
                      {slowMovingList.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 rounded-lg bg-rose-500/5 border border-rose-500/10"
                        >
                          <p
                            className={`text-[11px] font-bold ${textColorClass} truncate w-32`}
                          >
                            {item.name}
                          </p>
                          <span className="text-rose-500 font-bold text-[10px]">
                            {item.unit} Units
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </MetricCard>
              </div>

              <div className="md:col-span-5 w-full h-full max-md:min-h-52 col-span-12">
                <MetricCard
                  title="Inventory of Stock"
                  borderThem={borderThem}
                  themeMode={themeMode}
                >
                  <div className="h-40 mt-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={categoryChartData}>
                        <XAxis
                          dataKey="name"
                          fontSize={10}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip cursor={{ fill: "transparent" }} />
                        <Bar
                          dataKey="stock"
                          fill="#6366f1"
                          radius={[4, 4, 0, 0]}
                          barSize={30}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </MetricCard>
              </div>
            </div>

            <div
              className={` ${bgColorClass} ${borderThem} rounded-xl border shadow-sm overflow-hidden`}
            >
              <div
                className={`px-4 py-2 border-b ${borderThem} flex max-md:flex-col justify-between md:items-center`}
              >
                <h3
                  className={`font-bold text-sm ${textColorClass} max-md:mb-2`}
                >
                  Stock Replenishment Details
                </h3>
                <div className="relative">
                  <Search
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
                    size={14}
                  />
                  <input
                    className={`pl-7 pr-3 py-1.5 border ${textColorClass} ${borderThem} rounded-lg text-xs outline-none focus:ring-1 ring-indigo-500`}
                    placeholder="Search product..."
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className={`overflow-x-auto ${scrollBg}`}>
                <table className="w-full text-left table-auto border-collapse">
                  <thead>
                    <tr
                      className={`${tableHedBg} text-[10px] ${borderThem} uppercase tracking-wider ${textColorClass} font-bold border-b`}
                    >
                      <th className="px-4 py-1.5 w-10">
                        <input
                          type="checkbox"
                          className="rounded cursor-pointer"
                          onChange={toggleAll}
                          checked={
                            selectedIds.length === inventoryData.length &&
                            inventoryData.length > 0
                          }
                        />
                      </th>
                      <th className="px-4 py-1.5 min-w-72">Product</th>
                      <th className="px-4 py-1.5 min-w-20">Category</th>
                      <th className="px-4 py-1.5 min-w-60">Location</th>
                      <th className="px-4 py-1.5 min-w-20">Unit</th>
                      <th className="px-4 py-1.5 min-w-24">On Hand</th>
                      <th className="px-4 py-1.5 min-w-20">Forecast</th>
                      <th className="px-4 py-1.5 min-w-20">Route</th>
                      <th className="px-4 py-1.5 min-w-20">Min</th>
                      <th className="px-4 py-1.5 min-w-20">Max</th>
                      <th className="px-4 py-1.5 min-w-24">To Order</th>
                      <th className="px-4 py-1.5 min-w-20 text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {inventoryData
                      .filter((i) =>
                        i.name.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((row, idx) => (
                        <tr
                          key={idx}
                          className={`hover:bg-slate-50/50 ${borderThem} transition-colors text-xs ${
                            row.forecast < 0 ? "bg-rose-50/30" : ""
                          }`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              className="rounded cursor-pointer"
                              checked={selectedIds.includes(row.id)}
                              onChange={() => toggleRow(row.id)}
                            />
                          </td>
                          <td
                            className={`px-4 py-3 ${textColorClass} font-medium`}
                          >
                            <span className="text-slate-400 mr-1">
                              [{row.id}]
                            </span>{" "}
                            {row.name}
                          </td>
                          <td
                            className={`px-4 py-3 ${textColorClass} opacity-60`}
                          >
                            {row.category}
                          </td>
                          <td className={`px-4 py-3 ${textColorClass}`}>
                            {row.location}
                          </td>
                          <td className={`px-4 py-3 ${textColorClass}`}>
                            {row.unit}
                          </td>
                          <td
                            className={`px-4 py-3 font-mono font-bold ${textColorClass}`}
                          >
                            {row.onHand.toFixed(2)}
                          </td>
                          <td
                            className={`px-4 py-3 font-mono font-bold flex items-center gap-1 ${
                              row.forecast < 0
                                ? "text-rose-500"
                                : "text-emerald-500"
                            }`}
                          >
                            {row.forecast.toFixed(2)} <LineIcon size={12} />
                          </td>
                          <td className="px-4 py-3">
                            {row.route && (
                              <span className="px-2 py-0.5 bg-slate-100 rounded text-[10px] font-bold text-slate-600">
                                {row.route}
                              </span>
                            )}
                          </td>
                          <td
                            className={`px-4 py-3 ${textColorClass} font-mono`}
                          >
                            {row.min.toFixed(2)}
                          </td>
                          <td
                            className={`px-4 py-3 ${textColorClass} font-mono`}
                          >
                            {row.max.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 font-bold text-indigo-600 font-mono">
                            {row.toOrder.toFixed(2)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-3">
                              <ShoppingCart
                                size={14}
                                className="text-indigo-600 cursor-pointer"
                                onClick={() => handleAction("Order", row.name)}
                              />
                              <Zap
                                size={14}
                                className="text-emerald-500 cursor-pointer"
                              />
                              <Moon
                                size={14}
                                className="text-slate-400 cursor-pointer"
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ title, children, borderThem }) => (
  <div className={`${borderThem} p-3 size-full rounded-xl border shadow-sm`}>
    <div className="flex justify-between items-center mb-2">
      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest">
        {title}
      </h3>
      <Info size={14} className="text-slate-300" />
    </div>
    {children}
  </div>
);

export default WarehouseAnalytics;
